/**
 * FooterAd Component
 * Horizontal ad strip placed above the page footer
 */

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAdTracking } from "@/hooks/useAdTracking";
import type { Advertisement } from "@/types/advertising";

interface FooterAdProps {
  ad: Advertisement;
  className?: string;
}

const FooterAd: React.FC<FooterAdProps> = ({ ad, className = "" }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad.id,
    placement: ad.placement,
    targetUrl: ad.targetUrl,
  });

  if (!ad.isActive) return null;

  const renderContent = () => {
    if (ad.format === "image" && ad.imageUrl && !imageError) {
      return (
        <div className="relative w-full">
          {isLoading && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ad.imageUrl}
            alt={ad.altText || ad.title}
            className="w-full h-auto block rounded-lg"
            onError={() => {
              setImageError(true);
              setIsLoading(false);
            }}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      );
    }

    if (ad.format === "video" && ad.videoUrl) {
      return (
        <div className="relative w-full">
          <video
            src={ad.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-auto block rounded-lg"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col md:flex-row items-center justify-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg h-[90px] md:h-[120px]">
        <h3 className="text-lg font-bold text-gray-900">{ad.title}</h3>
        {ad.description && (
          <p className="hidden md:block text-sm text-gray-600">
            {ad.description}
          </p>
        )}
        <span className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 transition-colors">
          Learn More
        </span>
      </div>
    );
  };

  return (
    <div
      ref={adRef}
      className={`footer-ad relative w-full my-6 ${className}`}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      role="complementary"
      aria-label="Footer Advertisement"
    >
      <div className="max-w-[1280px] mx-auto px-4">
        <div className="relative">
          <div className="absolute top-2 right-2 z-10">
            <span className="text-xs bg-gray-800 bg-opacity-75 text-white px-2 py-1 rounded">
              Ad
            </span>
          </div>
          <Link
            href={ad.targetUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={(e) => handleClick(e)}
            className="block w-full transition-transform hover:scale-[1.01] duration-200"
            aria-label={`Advertisement: ${ad.title}`}
          >
            {renderContent()}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FooterAd;
